import React, { useEffect, useState, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import {
    Box,
    Container,
    Typography,
    Paper,
    Grid,
    Divider,
    Alert,
    CircularProgress,
    Card,
    CardContent,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    useTheme,
    SpeedDial,
    SpeedDialIcon,
    SpeedDialAction,
    IconButton,
    Menu,
    MenuItem,
    Backdrop
} from '@mui/material';
import {
    PictureAsPdf as PdfIcon,
    MoreVert as MoreVertIcon
} from '@mui/icons-material';
import { projectService } from '../services/projectService';
import { formatCurrency } from '../utils/format';
import { formatDateBR } from '../utils/date';
import { ProjectSimulation } from '../types/project';
import { pdf } from '@react-pdf/renderer';
import ProposalPDF from '../components/ProposalPDF';
import {
    getSectionsByToken,
    createSectionByToken,
    updateSectionByToken,
    rewriteSectionByToken,
    deleteSectionByToken
} from '../services/proposalService';

interface SharedProposalData {
    project: {
        name: string;
        description: string;
        client?: {
            name: string;
        };
        simulation?: ProjectSimulation;
    };
    sections: {[key: string]: boolean};
    content: {[key: string]: any};
    created_at: string;
    expires_at: string;
}

const SharedProposalPage: React.FC = () => {
    const { token } = useParams<{token: string}>();
    const [loading, setLoading] = useState(true);
    const [exporting, setExporting] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [data, setData] = useState<SharedProposalData | null>(null);
    const [sections, setSections] = useState<any[]>([]);
    const [loadingSections, setLoadingSections] = useState(true);
    const [creatingSection, setCreatingSection] = useState<false | 'manual' | 'ia'>(false);
    const [newSectionTitle, setNewSectionTitle] = useState('');
    const [newSectionContent, setNewSectionContent] = useState('');
    const [newSectionPrompt, setNewSectionPrompt] = useState('');
    const [editingSection, setEditingSection] = useState<any | null>(null);
    const [rewriteInstruction, setRewriteInstruction] = useState('');
    const [rewritingSectionId, setRewritingSectionId] = useState<number | null>(null);
    const theme = useTheme();
    const { t } = useTranslation();
    const contentRef = useRef<HTMLDivElement>(null);
    
    // Menu state
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
    const open = Boolean(anchorEl);
    
    const handleClick = (event: React.MouseEvent<HTMLElement>) => {
        setAnchorEl(event.currentTarget);
    };
    
    const handleClose = () => {
        setAnchorEl(null);
    };

    const exportToPDF = async () => {
        if (!data) return;
        setExporting(true);

        try {
            const blob = await pdf(<ProposalPDF data={data} />).toBlob();
            const url = URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = 'qaracter-proposal.pdf';
            link.click();
            URL.revokeObjectURL(url);
        } catch (error) {
            console.error('Error generating PDF:', error);
        } finally {
            setExporting(false);
        }
    };

    // Edição das seções só para gestor/diretor logado (o backend exige o mesmo); o cliente só visualiza
    const isLoggedIn = (() => {
        try {
            const token = localStorage.getItem('@App:token');
            const role = JSON.parse(localStorage.getItem('@App:user') || 'null')?.role;
            return Boolean(token) && ['Gestor', 'Diretor'].includes(role);
        } catch {
            return false;
        }
    })();

    useEffect(() => {
        loadProposal();
    }, [token]);

    useEffect(() => {
        if (token) {
            loadSections();
        }
    }, [token]);

    const loadProposal = async () => {
        if (!token) return;

        try {
            const response = await projectService.getSharedProposal(token);
            setData(response);
        } catch (error) {
            console.error('Error loading proposal:', error);
            setError('Proposta não encontrada ou expirada');
        } finally {
            setLoading(false);
        }
    };

    const loadSections = async () => {
        setLoadingSections(true);
        try {
            const result = await getSectionsByToken(token!);
            setSections(result);
        } catch (e) {
            setSections([]);
        } finally {
            setLoadingSections(false);
        }
    };

    // CRUD handlers
    const handleCreateSection = async (useAI = false) => {
        if (!token) return;
        const ordem = sections.length + 1;
        try {
            if (useAI) {
                await createSectionByToken(token, { titulo: newSectionTitle, prompt: newSectionPrompt, ordem });
            } else {
                await createSectionByToken(token, { titulo: newSectionTitle, conteudo: newSectionContent, ordem });
            }
            await loadSections();
            setCreatingSection(false);
            setNewSectionTitle('');
            setNewSectionContent('');
            setNewSectionPrompt('');
        } catch (error) {
            console.error('Error creating section:', error);
            alert('Erro ao criar seção. Tente novamente.');
        }
    };

    const handleEditSection = (section: any) => {
        setEditingSection(section);
        setNewSectionTitle(section.titulo);
        setNewSectionContent(section.conteudo);
    };

    const handleSaveEditSection = async () => {
        if (!token || !editingSection) return;
        await updateSectionByToken(token, editingSection.id, { titulo: newSectionTitle, conteudo: newSectionContent });
        setEditingSection(null);
        setNewSectionTitle('');
        setNewSectionContent('');
        await loadSections();
    };

    const handleDeleteSection = async (sectionId: number) => {
        if (!token) return;
        await deleteSectionByToken(token, sectionId);
        await loadSections();
    };

    const handleRewriteSection = async (sectionId: number) => {
        if (!token || !rewriteInstruction) return;
        setRewritingSectionId(sectionId);
        await rewriteSectionByToken(token, sectionId, rewriteInstruction);
        setRewriteInstruction('');
        setRewritingSectionId(null);
        await loadSections();
    };

    // Data pura da API lida como local (sem isso o cliente via o dia anterior)
    const formatDate = (dateString: string | null | undefined) => formatDateBR(dateString);

    if (loading) {
        return (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="100vh">
                <CircularProgress />
            </Box>
        );
    }

    if (error || !data) {
        return (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="100vh">
                <Alert severity="error">{error || t('errors.unexpectedError')}</Alert>
            </Box>
        );
    }

    // Render seções
    const renderSections = () => {
        if (loadingSections) {
            return (
                <Box display="flex" justifyContent="center" alignItems="center" py={4}>
                    <CircularProgress />
                </Box>
            );
        }
        if (!sections.length) {
            return (
                <Box mb={4}>
                    <Paper elevation={1} sx={{ p: 3, textAlign: 'center' }}>
                        <Typography variant="h6" color="textSecondary">
                            Nenhuma seção personalizada encontrada.
                        </Typography>
                        {isLoggedIn && (
                            <Typography variant="body2" color="textSecondary" mt={1}>
                                Use os botões abaixo para adicionar seções personalizadas à proposta.
                            </Typography>
                        )}
                    </Paper>
                </Box>
            );
        }
        return sections.sort((a, b) => a.ordem - b.ordem).map(section => (
            <Box key={section.id} mb={4}>
                <Paper elevation={1} sx={{ p: 3, position: 'relative' }}>
                    {editingSection && editingSection.id === section.id ? (
                        <Box component="form" onSubmit={e => { e.preventDefault(); handleSaveEditSection(); }}>
                            <Typography variant="h5" gutterBottom sx={{ fontWeight: 'bold' }}>
                                <input
                                    type="text"
                                    value={newSectionTitle}
                                    onChange={e => setNewSectionTitle(e.target.value)}
                                    style={{
                                        width: '100%',
                                        fontSize: '1.5rem',
                                        fontWeight: 'bold',
                                        marginBottom: 16,
                                        padding: 8,
                                        border: '1px solid #ccc',
                                        borderRadius: 4
                                    }}
                                    placeholder="Título da seção"
                                    required
                                />
                            </Typography>
                            <textarea
                                value={newSectionContent}
                                onChange={e => setNewSectionContent(e.target.value)}
                                style={{
                                    width: '100%',
                                    minHeight: 180,
                                    fontSize: '1rem',
                                    padding: 12,
                                    border: '1px solid #ccc',
                                    borderRadius: 4,
                                    marginBottom: 16,
                                    fontFamily: 'inherit',
                                    resize: 'vertical'
                                }}
                                placeholder="Conteúdo HTML da seção"
                                required
                            />
                            <Box display="flex" gap={2}>
                                <button
                                    type="submit"
                                    style={{
                                        padding: '10px 20px',
                                        backgroundColor: theme.palette.primary.main,
                                        color: 'white',
                                        border: 'none',
                                        borderRadius: '4px',
                                        cursor: 'pointer',
                                        fontWeight: 'bold'
                                    }}
                                >
                                    Salvar
                                </button>
                                <button
                                    type="button"
                                    onClick={() => setEditingSection(null)}
                                    style={{
                                        padding: '10px 20px',
                                        backgroundColor: '#666',
                                        color: 'white',
                                        border: 'none',
                                        borderRadius: '4px',
                                        cursor: 'pointer'
                                    }}
                                >
                                    Cancelar
                                </button>
                            </Box>
                        </Box>
                    ) : (
                        <>
                            <Typography variant="h5" gutterBottom sx={{ fontWeight: 'bold' }}>
                                {section.titulo}
                            </Typography>
                            <div 
                                dangerouslySetInnerHTML={{ __html: section.conteudo }} 
                                style={{ lineHeight: 1.6 }}
                            />
                            {isLoggedIn && (
                                <Box mt={3} display="flex" gap={2} flexWrap="wrap">
                                    <button 
                                        onClick={() => handleEditSection(section)}
                                        style={{
                                            padding: '8px 16px',
                                            backgroundColor: theme.palette.primary.main,
                                            color: 'white',
                                            border: 'none',
                                            borderRadius: '4px',
                                            cursor: 'pointer'
                                        }}
                                    >
                                        Editar
                                    </button>
                                    <button 
                                        onClick={() => handleDeleteSection(section.id)}
                                        style={{
                                            padding: '8px 16px',
                                            backgroundColor: '#f44336',
                                            color: 'white',
                                            border: 'none',
                                            borderRadius: '4px',
                                            cursor: 'pointer'
                                        }}
                                    >
                                        Excluir
                                    </button>
                                    <input
                                        type="text"
                                        placeholder="Instrução para IA"
                                        value={rewritingSectionId === section.id ? rewriteInstruction : ''}
                                        onChange={e => setRewriteInstruction(e.target.value)}
                                        disabled={rewritingSectionId !== null && rewritingSectionId !== section.id}
                                        style={{ 
                                            padding: '8px 12px',
                                            border: '1px solid #ccc',
                                            borderRadius: '4px',
                                            flex: 1,
                                            minWidth: '200px'
                                        }}
                                    />
                                    <button
                                        onClick={() => handleRewriteSection(section.id)}
                                        disabled={rewritingSectionId !== null && rewritingSectionId !== section.id || !rewriteInstruction}
                                        style={{
                                            padding: '8px 16px',
                                            backgroundColor: theme.palette.secondary.main,
                                            color: 'white',
                                            border: 'none',
                                            borderRadius: '4px',
                                            cursor: 'pointer',
                                            opacity: (rewritingSectionId !== null && rewritingSectionId !== section.id || !rewriteInstruction) ? 0.5 : 1
                                        }}
                                    >
                                        {rewritingSectionId === section.id ? 'Reescrevendo...' : 'Reescrever IA'}
                                    </button>
                                </Box>
                            )}
                        </>
                    )}
                </Paper>
            </Box>
        ));
    };

    return (
        <>
            <Container maxWidth="lg" sx={{ py: 6 }} ref={contentRef}>
                {/* Cabeçalho da Proposta */}
                <Box 
                    component={Paper} 
                    elevation={0}
                    sx={{
                        p: 6,
                        mb: 4,
                        background: `linear-gradient(45deg, ${theme.palette.primary.main}ee, ${theme.palette.secondary.main}ee), url('/fundo.png')`,
                        backgroundSize: 'cover',
                        backgroundPosition: 'center',
                        '&::before': {
                            content: '""',
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            right: 0,
                            bottom: 0,
                            backgroundImage: 'url(/fundo.png)',
                            backgroundSize: 'cover',
                            backgroundPosition: 'center',
                            filter: 'brightness(0) invert(1)',
                            opacity: 0.1,
                            zIndex: 0
                        },
                        color: 'white',
                        borderRadius: 2,
                        position: 'relative',
                        overflow: 'hidden'
                    }}
                >
                    <Grid container spacing={4} sx={{ position: 'relative', zIndex: 1 }}>
                        <Grid item xs={12}>
                            <Box mb={4}>
                                <Box 
                                    component="img"
                                    src="/logo.svg"
                                    alt="Qaracter"
                                    sx={{
                                        height: 50,
                                        width: 'auto',
                                        mb: 4
                                    }}
                                />
                                <Typography 
                                    variant="overline" 
                                    sx={{ 
                                        opacity: 0.8,
                                        letterSpacing: 2,
                                        display: 'block',
                                        mb: 1
                                    }}
                                >
                                    {t('proposal.title')}
                                </Typography>
                                <Typography 
                                    variant="h2"
                                    component="h1"
                                    sx={{
                                        fontWeight: 'bold',
                                        mb: 2,
                                        // No celular a fonte fixa de 60px cortava palavras longas do nome do projeto
                                        fontSize: { xs: '2rem', sm: '2.75rem', md: '3.75rem' },
                                        overflowWrap: 'anywhere',
                                        hyphens: 'auto',
                                    }}
                                >
                                    {data.project.name}
                                </Typography>
                                <Typography 
                                    variant="h5" 
                                    sx={{ 
                                        opacity: 0.9,
                                        maxWidth: '800px',
                                        lineHeight: 1.6
                                    }}
                                >
                                    {data.project.description}
                    </Typography>
                            </Box>
                            <Box>
                                <Typography variant="subtitle1" sx={{ opacity: 0.8 }}>
                                    {t('proposal.preparedFor')}: {data.project.client?.name}
                                </Typography>
                                <Typography variant="subtitle1" sx={{ opacity: 0.8 }}>
                                    {t('proposal.date')}: {formatDate(data.created_at)}
                                </Typography>
                            </Box>
                        </Grid>
                    </Grid>
                </Box>

                {/* Índice */}
                <Box 
                    component={Paper} 
                    elevation={0}
                    sx={{
                        display: 'flex',
                        mb: 4,
                        backgroundColor: 'background.paper',
                        borderRadius: 2,
                        position: 'relative',
                        overflow: 'hidden',
                        minHeight: '100vh'
                    }}
                >
                    <Box
                        sx={{
                            flex: '0 0 58.333%',
                            p: 6,
                            position: 'relative',
                            zIndex: 1
                        }}
                    >
                        <Typography 
                            variant="h3" 
                            component="h2" 
                            sx={{ 
                                color: theme.palette.text.primary,
                                fontWeight: 'bold',
                                position: 'relative',
                                mb: 6,
                                '&::after': {
                                    content: '""',
                                    position: 'absolute',
                                    bottom: -16,
                                    left: 0,
                                    width: '60px',
                                    height: '4px',
                                    backgroundColor: theme.palette.secondary.main,
                                    borderRadius: '2px'
                                }
                            }}
                        >
                            Índice
                                </Typography>

                        <Box sx={{ position: 'relative' }}>
                            {/* Seções fixas e dinâmicas na ordem correta */}
                            {[
                                { id: 'qaracter', title: 'Qaracter' },
                                { id: 'scope', title: 'Escopo' },
                                { id: 'methodology', title: 'Metodologia' },
                                { id: 'strategy', title: 'Estratégia' },
                                { id: 'architecture', title: 'Arquitetura' },
                                { id: 'team', title: 'Time' },
                                { id: 'timeline', title: 'Calendário' },
                                { id: 'financial', title: 'Finanças' }
                            ]
                            .filter(section => data.sections[section.id])  // Filtra apenas as seções selecionadas
                            .map((section, index) => {
                                return (
                                    <Box 
                                        key={section.id}
                                        sx={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            mb: 4,
                                            p: 2,
                                            borderRadius: 1,
                                            transition: 'all 0.3s ease',
                                            cursor: 'pointer',
                                            position: 'relative',
                                            '&:hover': {
                                                backgroundColor: 'rgba(0, 0, 0, 0.02)',
                                                transform: 'translateX(8px)',
                                                '& .section-number': {
                                                    color: theme.palette.secondary.main,
                                                },
                                                '& .section-title': {
                                                    color: theme.palette.secondary.main,
                                                }
                                            },
                                            '&::before': {
                                                content: '""',
                                                position: 'absolute',
                                                left: -20,
                                                top: '50%',
                                                width: 0,
                                                height: 2,
                                                backgroundColor: theme.palette.secondary.main,
                                                transition: 'width 0.3s ease',
                                                transform: 'translateY(-50%)'
                                            },
                                            '&:hover::before': {
                                                width: 16
                                            }
                                        }}
                                    >
                                        <Typography 
                                            variant="h5" 
                                            className="section-number"
                                            sx={{ 
                                                color: theme.palette.text.secondary,
                                                fontWeight: 'bold',
                                                mr: 3,
                                                transition: 'color 0.3s ease',
                                                fontSize: '2rem'
                                            }}
                                        >
                                            {(index + 1)}.
                                    </Typography>
                                        <Typography 
                                            variant="h5"
                                            className="section-title"
                                            sx={{ 
                                                color: theme.palette.text.primary,
                                                transition: 'color 0.3s ease',
                                                fontSize: '1.5rem',
                                                fontWeight: '500'
                                            }}
                                        >
                                            {section.title}
                                    </Typography>
                                    </Box>
                                );
                            })}
                        </Box>
                    </Box>

                    <Box
                        sx={{
                            flex: '0 0 41.667%',
                            position: 'relative',
                            display: { xs: 'none', md: 'block' },
                            height: 'auto'
                        }}
                    >
                        <Box
                            component="img"
                            src="/secao2.jpg"
                            alt="Índice"
                            sx={{
                                position: 'absolute',
                                top: 0,
                                right: 0,
                                width: '100%',
                                height: '100%',
                                objectFit: 'cover'
                            }}
                        />
                    </Box>
                </Box>

                {/* Qaracter Section */}
                <Box 
                    component={Paper} 
                    elevation={0}
                    sx={{
                        mb: 4,
                        borderRadius: 2,
                        position: 'relative',
                        overflow: 'hidden',
                        minHeight: '100vh',
                        color: 'white',
                        '&::before': {
                            content: '""',
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            right: 0,
                            bottom: 0,
                            backgroundImage: 'url(/mapa.jpg)',
                            backgroundSize: 'cover',
                            backgroundPosition: 'center',
                            zIndex: 0
                        },
                        '&::after': {
                            content: '""',
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            right: 0,
                            bottom: 0,
                            background: `linear-gradient(45deg, ${theme.palette.primary.main}ee, ${theme.palette.secondary.main}dd)`,
                            zIndex: 1
                        }
                    }}
                >
                    <Box
                        sx={{
                            position: 'relative',
                            zIndex: 2,
                            p: 6
                        }}
                    >
                        <Typography 
                            variant="h3" 
                            component="h2" 
                            sx={{ 
                                color: 'white',
                                fontWeight: 'bold',
                                position: 'relative',
                                mb: 6,
                                '&::after': {
                                    content: '""',
                                    position: 'absolute',
                                    bottom: -16,
                                    left: 0,
                                    width: '60px',
                                    height: '4px',
                                    backgroundColor: 'white',
                                    borderRadius: '2px'
                                }
                            }}
                        >
                            Qaracter
                                    </Typography>

                        <Box sx={{ position: 'relative', maxWidth: '1200px', mx: 'auto' }}>
                            {/* Presença e Números */}
                            <Box mb={6}>
                                <Typography variant="h5" gutterBottom sx={{ fontWeight: 'bold', color: 'white' }}>
                                    Presença e Números
                                </Typography>
                                <Typography paragraph sx={{ fontSize: '1.1rem', mb: 4, maxWidth: '800px', color: 'white' }}>
                                    Qaracter é uma consultoria internacional, especializada em serviços financeiros, que presta serviços de assessoria de negócios, riscos, tecnologia, processos e organização baseados no conhecimento do setor em que operam nossos clientes.
                                </Typography>

                                <Box mb={4}>
                                    <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold', color: 'white' }}>
                                        NÚMEROS DO GRUPO
                                    </Typography>
                                    <Box mb={3}>
                                        <Typography variant="subtitle1" sx={{ color: 'rgba(255, 255, 255, 0.8)' }}>EMPREGADOS</Typography>
                                        <Typography variant="h4" sx={{ fontWeight: 'bold', color: 'white' }}>
                                            1.000 profissionais
                                    </Typography>
                                </Box>
                                    <Box>
                                        <Typography variant="subtitle1" sx={{ color: 'rgba(255, 255, 255, 0.8)' }} gutterBottom>ESCRITÓRIOS</Typography>
                                        <Grid container spacing={2}>
                                            {['Espanha', 'Brasil', 'Estados Unidos', 'Argentina', 'México', 'Reino Unido', 'Polonia'].map((country) => (
                                                <Grid item xs={12} sm={6} key={country}>
                                                    <Box 
                                                        sx={{ 
                                                            display: 'flex', 
                                                            alignItems: 'center',
                                                            '&::before': {
                                                                content: '""',
                                                                display: 'inline-block',
                                                                width: '6px',
                                                                height: '6px',
                                                                borderRadius: '50%',
                                                                backgroundColor: 'white',
                                                                mr: 1
                                                            }
                                                        }}
                                                    >
                                                        <Typography sx={{ color: 'white' }}>{country}</Typography>
                                                    </Box>
                    </Grid>
                                            ))}
                                        </Grid>
                                    </Box>
                                </Box>

                                {/* Nossas Capacidades */}
                                <Box>
                                    <Typography variant="h5" gutterBottom sx={{ fontWeight: 'bold', color: 'white' }}>
                                        Nossas Capacidades
                                </Typography>
                                    <Typography paragraph sx={{ fontSize: '1.1rem', mb: 4, maxWidth: '800px', color: 'white' }}>
                                        Contamos com capacidades diferenciais no mercado financeiro que reúnem um amplo conhecimento funcional de produtos, processos, gestão de projetos, gestão de mudanças e execução de processos, juntamente com o conhecimento técnico e as parcerias necessárias para executar qualquer tipo de projeto ou serviço, ao longo de toda a cadeia de valor.
                                    </Typography>

                                    <Grid container spacing={3}>
                                        {[
                                            {
                                                number: '01',
                                                title: 'BUSINESS CONSULTING',
                                                items: ['Mercados', 'Produtos CIB', 'Regulatório', 'Riscos', 'PMO & Change Mgt']
                                            },
                                            {
                                                number: '02',
                                                title: 'TRANSFORMAÇÃO DIGITAL',
                                                items: ['Estratégia de Transformação', 'Processos Digitais', 'Digitalização & Automatização', 'Inteligência Artificial', 'BI & Analytics']
                                            },
                                            {
                                                number: '03',
                                                title: 'TECHNOLOGY CONSULTING',
                                                items: ['Desenvolvimento & Integração', 'Suporte & Manutenção', 'Testing & QA', 'Cloud, DevOps & Cyber']
                                            },
                                            {
                                                number: '04',
                                                title: 'VENDOR SOLUTIONS',
                                                items: ['Calypso', 'Lumina', 'Alveo', 'Tibco', 'Murex', 'GBO', 'Sistemas Integrados Investimentos e Riscos']
                                            },
                                            {
                                                number: '05',
                                                title: 'OPERAÇÕES',
                                                items: ['Reporting Regulatório', 'AML/CDD/FC', 'Trade Finance & Working Capital', 'Treasury & Finance']
                                            }
                                        ].map((area) => (
                                            <Grid item xs={12} key={area.title}>
                                                <Box 
                                                    sx={{
                                                        display: 'flex',
                                                        alignItems: 'flex-start',
                                                        mb: 3,
                                                        p: 2,
                                                        borderRadius: 1,
                                                        transition: 'all 0.3s ease',
                                                        cursor: 'pointer',
                                                        position: 'relative',
                                                        '&:hover': {
                                                            backgroundColor: 'rgba(255, 255, 255, 0.1)',
                                                            transform: 'translateX(8px)',
                                                        }
                                                    }}
                                                >
                                                    <Typography 
                                                        variant="h5" 
                                                        sx={{ 
                                                            color: 'white',
                                                            fontWeight: 'bold',
                                                            mr: 3,
                                                            minWidth: '40px'
                                                        }}
                                                    >
                                                        {area.number}
                                    </Typography>
                                                    <Box>
                                                        <Typography 
                                                            variant="h6" 
                                                            sx={{ 
                                                                fontWeight: 'bold',
                                                                mb: 1,
                                                                color: 'white'
                                                            }}
                                                        >
                                                            {area.title}
                                    </Typography>
                                                        <Grid container spacing={1}>
                                                            {area.items.map((item) => (
                                                                <Grid item xs={12} sm={6} md={4} key={item}>
                                                                    <Typography 
                                                                        variant="body2" 
                                                                        sx={{ 
                                                                            display: 'flex',
                                                                            alignItems: 'center',
                                                                            color: 'rgba(255, 255, 255, 0.9)',
                                                                            '&::before': {
                                                                                content: '""',
                                                                                display: 'inline-block',
                                                                                width: '4px',
                                                                                height: '4px',
                                                                                borderRadius: '50%',
                                                                                backgroundColor: 'white',
                                                                                mr: 1
                                                                            }
                                                                        }}
                                                                    >
                                                                        {item}
                                    </Typography>
                                                                </Grid>
                                                            ))}
                                                        </Grid>
                                </Box>
                                                </Box>
                    </Grid>
                                        ))}
                </Grid>
                                </Box>
                            </Box>
                        </Box>
                    </Box>
                </Box>

                {/* Sections in order */}
                {[
                    { id: 'scope', title: 'Escopo' },
                    { id: 'methodology', title: 'Metodologia' },
                    { id: 'strategy', title: 'Estratégia' },
                    { id: 'architecture', title: 'Arquitetura' },
                    { id: 'team', title: 'Time' },
                    { id: 'timeline', title: 'Calendário' },
                    { id: 'financial', title: 'Finanças' }
                ].map(section => {
                    if (!data?.sections[section.id]) return null;

                    if (section.id === 'scope') {
                        return (
                            <Box 
                                key={section.id}
                                component={Paper} 
                                elevation={0}
                                sx={{
                                    display: 'flex',
                                    mb: 4,
                                    backgroundColor: 'background.paper',
                                    borderRadius: 2,
                                    position: 'relative',
                                    overflow: 'hidden',
                                    minHeight: '100vh'
                                }}
                            >
                                <Box
                                    sx={{
                                        flex: '0 0 41.667%',
                                        position: 'relative',
                                        display: { xs: 'none', md: 'block' },
                                        height: 'auto'
                                    }}
                                >
                                    <Box
                                        component="img"
                                        src="/secao2.jpg"
                                        alt="Escopo"
                                        sx={{
                                            position: 'absolute',
                                            top: 0,
                                            left: 0,
                                            width: '100%',
                                            height: '100%',
                                            objectFit: 'cover'
                                        }}
                                    />
                                </Box>

                                <Box
                                    sx={{
                                        flex: '0 0 58.333%',
                                        p: 6,
                                        position: 'relative',
                                        zIndex: 1
                                    }}
                                >
                                    <Typography 
                                        variant="h3" 
                                        component="h2" 
                                        sx={{ 
                                            color: theme.palette.text.primary,
                                            fontWeight: 'bold',
                                            position: 'relative',
                                            mb: 6,
                                            '&::after': {
                                                content: '""',
                                                position: 'absolute',
                                                bottom: -16,
                                                left: 0,
                                                width: '60px',
                                                height: '4px',
                                                backgroundColor: theme.palette.secondary.main,
                                                borderRadius: '2px'
                                            }
                                        }}
                                    >
                                        {section.title}
                                    </Typography>

                                    <Box sx={{ position: 'relative' }}>
                                        {/* Main Description */}
                                        {data.content.scope?.description && (
                                            <Typography 
                                                paragraph 
                                                sx={{ 
                                                    fontSize: '1.1rem', 
                                                    mb: 4, 
                                                    maxWidth: '800px',
                                                    lineHeight: 1.8
                                                }}
                                            >
                                                {data.content.scope.description}
                                            </Typography>
                                        )}

                                        {/* Bullet Points */}
                                        {data.content.scope?.items && (
                                            <Box mt={4}>
                                                <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold', color: theme.palette.primary.main }}>
                                                    Para tal, a solução contempla minimamente:
                                                </Typography>
                                                <Box>
                                                    {data.content.scope.items.map((item: string, index: number) => (
                                                        <Box 
                                                            key={index}
                                                            sx={{
                                                                display: 'flex',
                                                                alignItems: 'flex-start',
                                                                mb: 3,
                                                                p: 2,
                                                                borderRadius: 1,
                                                                transition: 'all 0.3s ease',
                                                                position: 'relative',
                                                                '&:hover': {
                                                                    backgroundColor: 'rgba(0, 0, 0, 0.02)',
                                                                    transform: 'translateX(8px)',
                                                                }
                                                            }}
                                                        >
                                                            <Typography 
                                                                sx={{ 
                                                                    color: theme.palette.secondary.main,
                                                                    fontWeight: 'bold',
                                                                    mr: 3,
                                                                    minWidth: '24px'
                                                                }}
                                                            >
                                                                {(index + 1).toString().padStart(2, '0')}
                                                            </Typography>
                                                            <Typography 
                                                                sx={{ 
                                                                    flex: 1,
                                                                    fontSize: '1.1rem',
                                                                    lineHeight: 1.6
                                                                }}
                                                            >
                                                                {item}
                                                            </Typography>
                                                        </Box>
                                                    ))}
                                                </Box>
                                            </Box>
                                        )}

                                        {/* Conclusion */}
                                        {data.content.scope?.conclusion && (
                                            <Typography 
                                                paragraph 
                                                sx={{ 
                                                    fontSize: '1.1rem', 
                                                    mt: 4, 
                                                    maxWidth: '800px',
                                                    lineHeight: 1.8
                                                }}
                                            >
                                                {data.content.scope.conclusion}
                                            </Typography>
                                        )}
                                    </Box>
                                </Box>
                            </Box>
                        );
                    }

                    if (section.id === 'methodology') {
                        return (
                            <Box 
                                key={section.id}
                                component={Paper} 
                                elevation={0}
                                sx={{
                                    display: 'flex',
                                    mb: 4,
                                    backgroundColor: 'background.paper',
                                    borderRadius: 2,
                                    position: 'relative',
                                    overflow: 'hidden',
                                    minHeight: '100vh'
                                }}
                            >
                                <Box
                                    sx={{
                                        flex: '0 0 58.333%',
                                        p: 6,
                                        position: 'relative',
                                        zIndex: 1
                                    }}
                                >
                                    <Typography 
                                        variant="h3" 
                                        component="h2" 
                                        sx={{ 
                                            color: theme.palette.text.primary,
                                            fontWeight: 'bold',
                                            position: 'relative',
                                            mb: 6,
                                            '&::after': {
                                                content: '""',
                                                position: 'absolute',
                                                bottom: -16,
                                                left: 0,
                                                width: '60px',
                                                height: '4px',
                                                backgroundColor: theme.palette.secondary.main,
                                                borderRadius: '2px'
                                            }
                                        }}
                                    >
                                        {section.title}
                                    </Typography>

                                    <Box sx={{ position: 'relative' }}>
                                        {/* Main Description */}
                                        {data.content.methodology?.description && (
                                            <Typography 
                                                paragraph 
                                                sx={{ 
                                                    fontSize: '1.1rem', 
                                                    mb: 4, 
                                                    maxWidth: '800px',
                                                    lineHeight: 1.8
                                                }}
                                            >
                                                {data.content.methodology.description}
                                            </Typography>
                                        )}

                                        {/* Milestones */}
                                        {data.content.methodology?.milestones && (
                                            <Box mt={4}>
                                                <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold', color: theme.palette.primary.main }}>
                                                    Marcos do Projeto
                                                </Typography>
                                                <Box>
                                                    {(typeof data.content.methodology.milestones === 'string' 
                                                        ? data.content.methodology.milestones.split('\n').filter((line: string) => line.trim())
                                                        : data.content.methodology.milestones
                                                    ).map((milestone: string, index: number) => (
                                                        <Box 
                                                            key={index}
                                                            sx={{
                                                                display: 'flex',
                                                                alignItems: 'flex-start',
                                                                mb: 3,
                                                                p: 2,
                                                                borderRadius: 1,
                                                                transition: 'all 0.3s ease',
                                                                position: 'relative',
                                                                '&:hover': {
                                                                    backgroundColor: 'rgba(0, 0, 0, 0.02)',
                                                                    transform: 'translateX(8px)',
                                                                }
                                                            }}
                                                        >
                                                            <Box
                                                                sx={{
                                                                    width: 32,
                                                                    height: 32,
                                                                    borderRadius: '50%',
                                                                    backgroundColor: theme.palette.secondary.main,
                                                                    display: 'flex',
                                                                    alignItems: 'center',
                                                                    justifyContent: 'center',
                                                                    mr: 3,
                                                                    color: 'white',
                                                                    fontWeight: 'bold'
                                                                }}
                                                            >
                                                                {index + 1}
                                                            </Box>
                                                            <Typography 
                                                                sx={{ 
                                                                    flex: 1,
                                                                    fontSize: '1.1rem',
                                                                    lineHeight: 1.6,
                                                                    pt: 0.5
                                                                }}
                                                            >
                                                                {milestone}
                                                            </Typography>
                                                        </Box>
                                                    ))}
                                                </Box>
                                            </Box>
                                        )}
                                    </Box>
                                </Box>

                                <Box
                                    sx={{
                                        flex: '0 0 41.667%',
                                        position: 'relative',
                                        display: { xs: 'none', md: 'block' },
                                        height: 'auto'
                                    }}
                                >
                                    <Box
                                        component="img"
                                        src="/metodologia.jpg"
                                        alt="Metodologia"
                                        sx={{
                                            position: 'absolute',
                                            top: 0,
                                            right: 0,
                                            width: '100%',
                                            height: '100%',
                                            objectFit: 'cover'
                                        }}
                                    />
                                </Box>
                            </Box>
                        );
                    }

                    if (section.id === 'strategy') {
                        return (
                            <Box 
                                key={section.id}
                                component={Paper} 
                                elevation={0}
                                sx={{
                                    display: 'flex',
                                    mb: 4,
                                    backgroundColor: 'background.paper',
                                    borderRadius: 2,
                                    position: 'relative',
                                    overflow: 'hidden',
                                    minHeight: '100vh'
                                }}
                            >
                                <Box
                                    sx={{
                                        flex: '0 0 41.667%',
                                        position: 'relative',
                                        display: { xs: 'none', md: 'block' },
                                        height: 'auto'
                                    }}
                                >
                                    <Box
                                        component="img"
                                        src="/estrategia.jpg"
                                        alt="Estratégia"
                                        sx={{
                                            position: 'absolute',
                                            top: 0,
                                            left: 0,
                                            width: '100%',
                                            height: '100%',
                                            objectFit: 'cover'
                                        }}
                                    />
                                </Box>

                                <Box
                                    sx={{
                                        flex: '0 0 58.333%',
                                        p: 6,
                                        position: 'relative',
                                        zIndex: 1
                                    }}
                                >
                                    <Typography 
                                        variant="h3" 
                                        component="h2" 
                                        sx={{ 
                                            color: theme.palette.text.primary,
                                            fontWeight: 'bold',
                                            position: 'relative',
                                            mb: 6,
                                            '&::after': {
                                                content: '""',
                                                position: 'absolute',
                                                bottom: -16,
                                                left: 0,
                                                width: '60px',
                                                height: '4px',
                                                backgroundColor: theme.palette.secondary.main,
                                                borderRadius: '2px'
                                            }
                                        }}
                                    >
                                        {section.title}
                                    </Typography>

                                    <Box sx={{ position: 'relative' }}>
                                        {/* Main Description */}
                                        {data.content.strategy?.description && (
                                            <Typography 
                                                paragraph 
                                                sx={{ 
                                                    fontSize: '1.1rem', 
                                                    mb: 4, 
                                                    maxWidth: '800px',
                                                    lineHeight: 1.8
                                                }}
                                            >
                                                {data.content.strategy.description}
                                            </Typography>
                                        )}

                                        {/* Strategy Highlights */}
                                        <Box 
                                            mt={4} 
                                            sx={{
                                                backgroundColor: 'rgba(0, 0, 0, 0.02)',
                                                p: 3,
                                                borderRadius: 2,
                                                border: `1px solid ${theme.palette.divider}`
                                            }}
                                        >
                                            <Typography 
                                                variant="h6" 
                                                gutterBottom 
                                                sx={{ 
                                                    color: theme.palette.primary.main,
                                                    fontWeight: 'bold'
                                                }}
                                            >
                                                Benefícios Estratégicos
                                            </Typography>
                                            <Box sx={{ mt: 2 }}>
                                                {[
                                                    'Alinhamento com objetivos de negócio',
                                                    'Otimização de processos',
                                                    'Redução de custos operacionais',
                                                    'Aumento de eficiência',
                                                    'Vantagem competitiva'
                                                ].map((benefit, index) => (
                                                    <Box 
                                                        key={index}
                                                        sx={{
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            mb: 2,
                                                            '&:last-child': { mb: 0 }
                                                        }}
                                                    >
                                                        <Box
                                                            sx={{
                                                                width: 24,
                                                                height: 24,
                                                                borderRadius: '50%',
                                                                backgroundColor: theme.palette.secondary.main,
                                                                display: 'flex',
                                                                alignItems: 'center',
                                                                justifyContent: 'center',
                                                                mr: 2,
                                                                color: 'white',
                                                                fontSize: '0.8rem',
                                                                fontWeight: 'bold'
                                                            }}
                                                        >
                                                            ✓
                                                        </Box>
                                                        <Typography sx={{ fontSize: '1.1rem' }}>
                                                            {benefit}
                                                        </Typography>
                                                    </Box>
                                                ))}
                                            </Box>
                                        </Box>
                                    </Box>
                                </Box>
                            </Box>
                        );
                    }

                    if (section.id === 'architecture') {
                        return (
                            <Box 
                                key={section.id}
                                component={Paper} 
                                elevation={0}
                                sx={{
                                    display: 'flex',
                                    mb: 4,
                                    backgroundColor: 'background.paper',
                                    borderRadius: 2,
                                    position: 'relative',
                                    overflow: 'hidden',
                                    minHeight: '100vh'
                                }}
                            >
                                <Box
                                    sx={{
                                        flex: '0 0 58.333%',
                                        p: 6,
                                        position: 'relative',
                                        zIndex: 1
                                    }}
                                >
                                    <Typography 
                                        variant="h3" 
                                        component="h2" 
                                        sx={{ 
                                            color: theme.palette.text.primary,
                                            fontWeight: 'bold',
                                            position: 'relative',
                                            mb: 6,
                                            '&::after': {
                                                content: '""',
                                                position: 'absolute',
                                                bottom: -16,
                                                left: 0,
                                                width: '60px',
                                                height: '4px',
                                                backgroundColor: theme.palette.secondary.main,
                                                borderRadius: '2px'
                                            }
                                        }}
                                    >
                                        {section.title}
                                    </Typography>

                                    <Box sx={{ position: 'relative' }}>
                                        {/* Main Description */}
                                        {data.content.architecture?.description && (
                                            <Typography 
                                                paragraph 
                                                sx={{ 
                                                    fontSize: '1.1rem', 
                                                    mb: 4, 
                                                    maxWidth: '800px',
                                                    lineHeight: 1.8
                                                }}
                                            >
                                                {data.content.architecture.description}
                                            </Typography>
                                        )}

                                        {/* Architecture Diagram */}
                                        {data.content.architecture?.diagram_url && (
                                            <Box mt={4}>
                                                <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold', color: theme.palette.primary.main }}>
                                                    Diagrama da Arquitetura
                                                </Typography>
                                                <Box
                                                    sx={{
                                                        mt: 2,
                                                        p: 2,
                                                        border: `1px solid ${theme.palette.divider}`,
                                                        borderRadius: 1,
                                                        backgroundColor: 'background.default'
                                                    }}
                                                >
                                                    <Box
                                                        component="img"
                                                        src={data.content.architecture.diagram_url}
                                                        alt="Diagrama da Arquitetura"
                                                        sx={{
                                                            width: '100%',
                                                            height: 'auto',
                                                            maxWidth: '100%',
                                                            display: 'block',
                                                            borderRadius: 1
                                                        }}
                                                    />
                                                </Box>
                                            </Box>
                                        )}

                                        {/* Additional Information Box */}
                                        <Box 
                                            mt={4} 
                                            sx={{
                                                backgroundColor: 'rgba(0, 0, 0, 0.02)',
                                                p: 3,
                                                borderRadius: 2,
                                                border: `1px solid ${theme.palette.divider}`
                                            }}
                                        >
                                            <Typography 
                                                variant="h6" 
                                                gutterBottom 
                                                sx={{ 
                                                    color: theme.palette.primary.main,
                                                    fontWeight: 'bold'
                                                }}
                                            >
                                                Pontos Importantes
                                            </Typography>
                                            <Typography sx={{ fontSize: '1.1rem', lineHeight: 1.6 }}>
                                                A arquitetura proposta foi desenhada considerando os mais altos padrões de segurança, 
                                                escalabilidade e performance, garantindo uma solução robusta e preparada para o futuro.
                                            </Typography>
                                        </Box>
                                    </Box>
                                </Box>

                                <Box
                                    sx={{
                                        flex: '0 0 41.667%',
                                        position: 'relative',
                                        display: { xs: 'none', md: 'block' },
                                        height: 'auto'
                                    }}
                                >
                                    <Box
                                        component="img"
                                        src="/arquitetura.png"
                                        alt="Arquitetura"
                                        sx={{
                                            position: 'absolute',
                                            top: 0,
                                            right: 0,
                                            width: '100%',
                                            height: '100%',
                                            objectFit: 'cover'
                                        }}
                                    />
                                </Box>
                            </Box>
                        );
                    }

                    if (section.id === 'team') {
                        return (
                            <Box 
                                key={section.id}
                                component={Paper} 
                                elevation={0}
                                sx={{
                                    mb: 4,
                                    backgroundColor: 'background.paper',
                                    borderRadius: 2,
                                    position: 'relative',
                                    overflow: 'hidden',
                                    p: 6
                                }}
                            >
                                <Typography 
                                    variant="h3" 
                                    component="h2" 
                                    sx={{ 
                                        color: theme.palette.text.primary,
                                        fontWeight: 'bold',
                                        position: 'relative',
                                        mb: 6,
                                        '&::after': {
                                            content: '""',
                                            position: 'absolute',
                                            bottom: -16,
                                            left: 0,
                                            width: '60px',
                                            height: '4px',
                                            backgroundColor: theme.palette.secondary.main,
                                            borderRadius: '2px'
                                        }
                                    }}
                                >
                                    {section.title}
                                </Typography>

                                <Box>
                                    {/* Main Description */}
                                    <Typography 
                                        paragraph 
                                        sx={{ 
                                            fontSize: '1.1rem', 
                                            mb: 4, 
                                            maxWidth: '800px',
                                            lineHeight: 1.8
                                        }}
                                    >
                                        Nossa equipe foi cuidadosamente dimensionada para atender às necessidades específicas do projeto, 
                                        garantindo a entrega com excelência e dentro do prazo estabelecido. Cada profissional foi selecionado 
                                        com base em sua experiência e especialização.
                                    </Typography>

                                    {/* Team Grid */}
                                    <Grid container spacing={3}>
                                        {data.project.simulation?.positions.map((position, index) => (
                                            <Grid item xs={12} sm={6} md={4} lg={3} key={index}>
                                                <Box
                                                    sx={{
                                                        p: 3,
                                                        border: `1px solid ${theme.palette.divider}`,
                                                        borderRadius: 2,
                                                        transition: 'all 0.3s ease',
                                                        height: '100%',
                                                        display: 'flex',
                                                        flexDirection: 'column',
                                                        '&:hover': {
                                                            transform: 'translateY(-4px)',
                                                            boxShadow: theme.shadows[4],
                                                            borderColor: theme.palette.secondary.main
                                                        }
                                                    }}
                                                >
                                                    <Box display="flex" alignItems="center" mb={2}>
                                                        <Box
                                                            sx={{
                                                                width: 48,
                                                                height: 48,
                                                                borderRadius: '12px',
                                                                backgroundColor: theme.palette.secondary.main,
                                                                display: 'flex',
                                                                alignItems: 'center',
                                                                justifyContent: 'center',
                                                                mr: 2,
                                                                color: 'white',
                                                                fontSize: '1.5rem',
                                                                fontWeight: 'bold'
                                                            }}
                                                        >
                                                            {position.role.charAt(0)}
                                                        </Box>
                                                        <Box>
                                                            <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 0.5 }}>
                                                                {position.role}
                                                            </Typography>
                                                        </Box>
                                                    </Box>

                                                    <Box 
                                                        sx={{ 
                                                            display: 'flex', 
                                                            flexDirection: 'column',
                                                            gap: 2,
                                                            mt: 'auto'
                                                        }}
                                                    >
                                                        <Box>
                                                            <Typography variant="caption" color="textSecondary" display="block">
                                                                Duração
                                                            </Typography>
                                                            <Typography variant="body1" fontWeight="medium">
                                                                {position.duration_months} meses
                                                            </Typography>
                                                        </Box>
                                                        <Box>
                                                            <Typography variant="caption" color="textSecondary" display="block">
                                                                Início
                                                            </Typography>
                                                            <Typography variant="body1" fontWeight="medium">
                                                                {formatDate(position.start_date)}
                                                            </Typography>
                                                        </Box>
                                                    </Box>
                                                </Box>
                                            </Grid>
                                        ))}
                                    </Grid>

                                    {/* Additional Information */}
                                    <Box 
                                        mt={4} 
                                        sx={{
                                            backgroundColor: 'rgba(0, 0, 0, 0.02)',
                                            p: 3,
                                            borderRadius: 2,
                                            border: `1px solid ${theme.palette.divider}`
                                        }}
                                    >
                                        <Typography 
                                            variant="h6" 
                                            gutterBottom 
                                            sx={{ 
                                                color: theme.palette.primary.main,
                                                fontWeight: 'bold'
                                            }}
                                        >
                                            Benefícios da Nossa Equipe
                                        </Typography>
                                        <Grid container spacing={3} sx={{ mt: 1 }}>
                                            {[
                                                'Profissionais altamente qualificados e experientes',
                                                'Equipe dimensionada para máxima eficiência',
                                                'Flexibilidade de alocação conforme necessidade',
                                                'Expertise comprovada em projetos similares',
                                                'Suporte contínuo durante todo o projeto'
                                            ].map((benefit, index) => (
                                                <Grid item xs={12} sm={6} key={index}>
                                                    <Box 
                                                        sx={{
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            gap: 2
                                                        }}
                                                    >
                                                        <Box
                                                            sx={{
                                                                width: 24,
                                                                height: 24,
                                                                borderRadius: '50%',
                                                                backgroundColor: theme.palette.secondary.main,
                                                                display: 'flex',
                                                                alignItems: 'center',
                                                                justifyContent: 'center',
                                                                flexShrink: 0,
                                                                color: 'white',
                                                                fontSize: '0.8rem',
                                                                fontWeight: 'bold'
                                                            }}
                                                        >
                                                            ✓
                                                        </Box>
                                                        <Typography sx={{ fontSize: '1.1rem' }}>
                                                            {benefit}
                                                        </Typography>
                                                    </Box>
                                                </Grid>
                                            ))}
                                        </Grid>
                                    </Box>
                                </Box>
                            </Box>
                        );
                    }

                    if (section.id === 'timeline') {
                        return (
                            <Box 
                                key={section.id}
                                component={Paper} 
                                elevation={0}
                                sx={{
                                    mb: 4,
                                    backgroundColor: 'background.paper',
                                    borderRadius: 2,
                                    position: 'relative',
                                    overflow: 'hidden',
                                    p: 6
                                }}
                            >
                                <Typography 
                                    variant="h3" 
                                    component="h2" 
                                    sx={{ 
                                        color: theme.palette.text.primary,
                                        fontWeight: 'bold',
                                        position: 'relative',
                                        mb: 6,
                                        '&::after': {
                                            content: '""',
                                            position: 'absolute',
                                            bottom: -16,
                                            left: 0,
                                            width: '60px',
                                            height: '4px',
                                            backgroundColor: theme.palette.secondary.main,
                                            borderRadius: '2px'
                                        }
                                    }}
                                >
                                    {section.title}
                                </Typography>

                                <Box>
                                    {/* Project Duration Info */}
                                    <Box 
                                        sx={{ 
                                            p: 3, 
                                            border: `1px solid ${theme.palette.divider}`,
                                            borderRadius: 2,
                                            mb: 4,
                                            backgroundColor: 'rgba(0, 0, 0, 0.02)'
                                        }}
                                    >
                                        <Grid container spacing={3}>
                                            <Grid item xs={12} sm={4}>
                                                <Typography variant="subtitle2" color="textSecondary">
                                                    Início do Projeto
                                                </Typography>
                                                <Typography variant="h6">
                                                    {formatDate(data.project.simulation?.start_date)}
                                                </Typography>
                                            </Grid>
                                            <Grid item xs={12} sm={4}>
                                                <Typography variant="subtitle2" color="textSecondary">
                                                    Duração
                                                </Typography>
                                                <Typography variant="h6">
                                                    {data.project.simulation?.duration_months} meses
                                                </Typography>
                                            </Grid>
                                            <Grid item xs={12} sm={4}>
                                                <Typography variant="subtitle2" color="textSecondary">
                                                    Término Previsto
                                                </Typography>
                                                <Typography variant="h6">
                                                    {formatDate(data.project.simulation?.end_date)}
                                                </Typography>
                                            </Grid>
                                        </Grid>
                                    </Box>

                                    {/* Timeline Flow */}
                                    <Box 
                                        sx={{ 
                                            mt: 4,
                                            position: 'relative',
                                            overflowX: 'auto',
                                            px: 4
                                        }}
                                    >
                                        <Box
                                            sx={{
                                                display: 'flex',
                                                flexDirection: 'row',
                                                minWidth: 'fit-content',
                                                pb: 2,
                                                position: 'relative',
                                                '&::before': {
                                                    content: '""',
                                                    position: 'absolute',
                                                    left: 0,
                                                    right: 0,
                                                    top: '2.5rem',
                                                    height: '4px',
                                                    background: `linear-gradient(to right, ${theme.palette.primary.main}22, ${theme.palette.primary.main}22)`,
                                                    borderRadius: '4px'
                                                },
                                                '&::after': {
                                                    content: '""',
                                                    position: 'absolute',
                                                    left: 0,
                                                    right: 0,
                                                    top: '2.5rem',
                                                    height: '4px',
                                                    background: `linear-gradient(to right, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
                                                    borderRadius: '4px',
                                                    opacity: 0.7
                                                }
                                            }}
                                        >
                                            {data.content.timeline?.deliverables?.map((monthDeliverables: string | string[], index: number) => {
                                                const monthDate = new Date(data.project.simulation?.start_date || '');
                                                monthDate.setMonth(monthDate.getMonth() + index);
                                                const monthName = monthDate.toLocaleString('pt-BR', { month: 'long', year: 'numeric' });

                                                // Convert string to array if needed and split by lines
                                                const deliverablesList = typeof monthDeliverables === 'string' 
                                                    ? monthDeliverables.split('\n').filter(d => d.trim())
                                                    : Array.isArray(monthDeliverables) 
                                                        ? monthDeliverables.filter(d => d.trim())
                                                        : [];

                                                return (
                                                    <Box 
                                                        key={index}
                                                        sx={{
                                                            width: 320,
                                                            mr: index === (data.content.timeline?.deliverables?.length || 0) - 1 ? 0 : 4,
                                                            position: 'relative',
                                                            '&:hover': {
                                                                '& .timeline-dot': {
                                                                    transform: 'scale(1.2)',
                                                                    boxShadow: theme.shadows[4]
                                                                },
                                                                '& .timeline-content': {
                                                                    transform: 'translateY(8px)',
                                                                    boxShadow: theme.shadows[4]
                                                                }
                                                            }
                                                        }}
                                                    >
                                                        <Box
                                                            sx={{
                                                                display: 'flex',
                                                                flexDirection: 'column',
                                                                alignItems: 'center',
                                                                position: 'relative',
                                                                zIndex: 1,
                                                                mb: 3
                                                            }}
                                                        >
                                                            <Box
                                                                className="timeline-dot"
                                                                sx={{
                                                                    width: '3.5rem',
                                                                    height: '3.5rem',
                                                                    borderRadius: '50%',
                                                                    background: `linear-gradient(45deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
                                                                    display: 'flex',
                                                                    alignItems: 'center',
                                                                    justifyContent: 'center',
                                                                    color: 'white',
                                                                    fontWeight: 'bold',
                                                                    fontSize: '1.2rem',
                                                                    transition: 'all 0.3s ease',
                                                                    boxShadow: `0 0 0 6px ${theme.palette.background.paper}, 0 0 0 8px ${theme.palette.primary.main}22`,
                                                                    mb: 2
                                                                }}
                                                            >
                                                                {index + 1}
                                                            </Box>
                                                            <Typography
                                                                variant="subtitle1"
                                                                sx={{
                                                                    color: theme.palette.text.secondary,
                                                                    fontWeight: 500,
                                                                    textTransform: 'capitalize'
                                                                }}
                                                            >
                                                                {monthName}
                                                            </Typography>
                                                        </Box>
                                                        
                                                        <Box 
                                                            className="timeline-content"
                                                            sx={{ 
                                                                p: 3,
                                                                border: `1px solid ${theme.palette.divider}`,
                                                                borderRadius: 2,
                                                                backgroundColor: 'background.paper',
                                                                transition: 'all 0.3s ease',
                                                                position: 'relative',
                                                                minHeight: 200,
                                                                display: 'flex',
                                                                flexDirection: 'column',
                                                                boxShadow: `0 2px 4px ${theme.palette.primary.main}11`
                                                            }}
                                                        >
                                                            <Typography 
                                                                variant="h6" 
                                                                sx={{ 
                                                                    mb: 3,
                                                                    color: theme.palette.primary.main,
                                                                    fontWeight: 'bold',
                                                                    position: 'relative',
                                                                    zIndex: 1,
                                                                    '&::after': {
                                                                        content: '""',
                                                                        position: 'absolute',
                                                                        bottom: -8,
                                                                        left: 0,
                                                                        width: 40,
                                                                        height: 3,
                                                                        background: `linear-gradient(to right, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
                                                                        borderRadius: 1
                                                                    }
                                                                }}
                                                            >
                                                                Entregáveis
                                                            </Typography>
                                                            
                                                            {deliverablesList.length > 0 ? (
                                                                <Box 
                                                                    sx={{ 
                                                                        position: 'relative', 
                                                                        zIndex: 1,
                                                                        flex: 1,
                                                                        display: 'flex',
                                                                        flexDirection: 'column',
                                                                        gap: 2
                                                                    }}
                                                                >
                                                                    {deliverablesList.map((deliverable: string, dIndex: number) => (
                                                                        <Box 
                                                                            key={dIndex}
                                                                            sx={{
                                                                                display: 'flex',
                                                                                alignItems: 'flex-start',
                                                                                '&:hover': {
                                                                                    '& .deliverable-dot': {
                                                                                        backgroundColor: theme.palette.secondary.main,
                                                                                        transform: 'scale(1.2)'
                                                                                    },
                                                                                    '& .deliverable-text': {
                                                                                        color: theme.palette.secondary.main
                                                                                    }
                                                                                }
                                                                            }}
                                                                        >
                                                                            <Box
                                                                                className="deliverable-dot"
                                                                                sx={{
                                                                                    width: 8,
                                                                                    height: 8,
                                                                                    borderRadius: '50%',
                                                                                    backgroundColor: theme.palette.primary.main,
                                                                                    mr: 2,
                                                                                    mt: 1,
                                                                                    flexShrink: 0,
                                                                                    transition: 'all 0.3s ease'
                                                                                }}
                                                                            />
                                                                            <Typography 
                                                                                className="deliverable-text"
                                                                                variant="body1"
                                                                                sx={{
                                                                                    color: theme.palette.text.primary,
                                                                                    lineHeight: 1.6,
                                                                                    transition: 'all 0.3s ease'
                                                                                }}
                                                                            >
                                                                                {deliverable}
                                                                            </Typography>
                                                                        </Box>
                                                                    ))}
                                                                </Box>
                                                            ) : (
                                                                <Typography 
                                                                    variant="body2" 
                                                                    color="textSecondary"
                                                                    sx={{ 
                                                                        fontStyle: 'italic',
                                                                        position: 'relative',
                                                                        zIndex: 1
                                                                    }}
                                                                >
                                                                    Nenhum entregável definido para este mês
                                                                </Typography>
                                                            )}
                                                        </Box>
                                                    </Box>
                                                );
                                            })}
                                        </Box>
                                    </Box>
                                </Box>
                            </Box>
                        );
                    }

                    if (section.id === 'financial') {
                        return (
                            <Box 
                                key={section.id}
                                component={Paper} 
                                elevation={0}
                                sx={{
                                    mb: 4,
                                    backgroundColor: 'background.paper',
                                    borderRadius: 2,
                                    position: 'relative',
                                    overflow: 'hidden',
                                    minHeight: '60vh'
                                }}
                            >
                                <Box
                                    sx={{
                                        p: 6,
                                        position: 'relative',
                                        zIndex: 1,
                                        display: 'flex',
                                        flexDirection: 'column',
                                        alignItems: 'center',
                                        textAlign: 'center'
                                    }}
                                >
                                    <Typography 
                                        variant="h3" 
                                        component="h2" 
                                        sx={{ 
                                            color: theme.palette.text.primary,
                                            fontWeight: 'bold',
                                            position: 'relative',
                                            mb: 6,
                                            '&::after': {
                                                content: '""',
                                                position: 'absolute',
                                                bottom: -16,
                                                left: '50%',
                                                transform: 'translateX(-50%)',
                                                width: '60px',
                                                height: '4px',
                                                backgroundColor: theme.palette.secondary.main,
                                                borderRadius: '2px'
                                            }
                                        }}
                                    >
                                        Investimento
                                    </Typography>

                                    <Typography 
                                        variant="h6" 
                                        sx={{ 
                                            maxWidth: '800px',
                                            mb: 8,
                                            color: theme.palette.text.secondary,
                                            lineHeight: 1.8
                                        }}
                                    >
                                        Apresentamos uma proposta de investimento transparente e objetiva, 
                                        estruturada para entregar o máximo valor ao seu negócio.
                                    </Typography>

                                    <Grid container spacing={4} justifyContent="center" maxWidth="1200px">
                                        <Grid item xs={12} md={4}>
                                            <Box
                                                sx={{
                                                    p: 4,
                                                    border: `2px solid ${theme.palette.divider}`,
                                                    borderRadius: 3,
                                                    height: '100%',
                                                    display: 'flex',
                                                    flexDirection: 'column',
                                                    alignItems: 'center',
                                                    transition: 'all 0.3s ease',
                                                    '&:hover': {
                                                        borderColor: theme.palette.primary.main,
                                                        transform: 'translateY(-8px)',
                                                        boxShadow: theme.shadows[4]
                                                    }
                                                }}
                                            >
                                                <Typography variant="h6" color="textSecondary" mb={3}>
                                                    Investimento Mensal
                                                </Typography>
                                                <Typography 
                                                    variant="h3" 
                                                    sx={{ 
                                                        fontWeight: 'bold',
                                                        color: theme.palette.primary.main,
                                                        mb: 2
                                                    }}
                                                >
                                                    {formatCurrency((data.project.simulation?.total_revenue || 0) / (data.project.simulation?.duration_months || 1))}
                                                </Typography>
                                                <Typography variant="body1" color="textSecondary">
                                                    por mês
                                                </Typography>
                                            </Box>
                                        </Grid>

                                        <Grid item xs={12} md={4}>
                                            <Box
                                                sx={{
                                                    p: 4,
                                                    background: `linear-gradient(45deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
                                                    borderRadius: 3,
                                                    height: '100%',
                                                    display: 'flex',
                                                    flexDirection: 'column',
                                                    alignItems: 'center',
                                                    color: 'white',
                                                    position: 'relative',
                                                    overflow: 'hidden',
                                                    transition: 'all 0.3s ease',
                                                    '&:hover': {
                                                        transform: 'translateY(-8px)',
                                                        boxShadow: theme.shadows[8]
                                                    },
                                                    '&::before': {
                                                        content: '""',
                                                        position: 'absolute',
                                                        top: 0,
                                                        left: 0,
                                                        right: 0,
                                                        bottom: 0,
                                                        background: 'url(/fundo.png)',
                                                        backgroundSize: 'cover',
                                                        opacity: 0.1
                                                    }
                                                }}
                                            >
                                                <Typography variant="h6" mb={3} sx={{ position: 'relative', zIndex: 1 }}>
                                                    Investimento Total
                                                </Typography>
                                                <Typography 
                                                    variant="h3" 
                                                    sx={{ 
                                                        fontWeight: 'bold',
                                                        mb: 2,
                                                        position: 'relative',
                                                        zIndex: 1
                                                    }}
                                                >
                                                    {formatCurrency(data.project.simulation?.total_revenue || 0)}
                                                </Typography>
                                                <Typography variant="body1" sx={{ position: 'relative', zIndex: 1 }}>
                                                    valor total
                                                </Typography>
                                            </Box>
                                        </Grid>

                                        <Grid item xs={12} md={4}>
                                            <Box
                                                sx={{
                                                    p: 4,
                                                    border: `2px solid ${theme.palette.divider}`,
                                                    borderRadius: 3,
                                                    height: '100%',
                                                    display: 'flex',
                                                    flexDirection: 'column',
                                                    alignItems: 'center',
                                                    transition: 'all 0.3s ease',
                                                    '&:hover': {
                                                        borderColor: theme.palette.primary.main,
                                                        transform: 'translateY(-8px)',
                                                        boxShadow: theme.shadows[4]
                                                    }
                                                }}
                                            >
                                                <Typography variant="h6" color="textSecondary" mb={3}>
                                                    Duração do Projeto
                                                </Typography>
                                                <Typography 
                                                    variant="h3" 
                                                    sx={{ 
                                                        fontWeight: 'bold',
                                                        color: theme.palette.primary.main,
                                                        mb: 2
                                                    }}
                                                >
                                                    {data.project.simulation?.duration_months || 0}
                                                </Typography>
                                                <Typography variant="body1" color="textSecondary">
                                                    meses
                                                </Typography>
                                            </Box>
                                        </Grid>
                                    </Grid>

                                    <Box 
                                        sx={{ 
                                            mt: 8,
                                            p: 4,
                                            backgroundColor: 'rgba(0, 0, 0, 0.02)',
                                            borderRadius: 3,
                                            maxWidth: '800px'
                                        }}
                                    >
                                        <Typography variant="h6" gutterBottom sx={{ color: theme.palette.primary.main, fontWeight: 'bold' }}>
                                            O Investimento Inclui:
                                        </Typography>
                                        <Grid container spacing={2} sx={{ mt: 2 }}>
                                            {[
                                                'Equipe dedicada e especializada',
                                                'Gestão completa do projeto',
                                                'Desenvolvimento e implementação',
                                                'Testes e garantia de qualidade',
                                                'Documentação detalhada',
                                                'Suporte durante todo o projeto'
                                            ].map((item, index) => (
                                                <Grid item xs={12} sm={6} key={index}>
                                                    <Box 
                                                        sx={{ 
                                                            display: 'flex', 
                                                            alignItems: 'center',
                                                            gap: 2
                                                        }}
                                                    >
                                                        <Box
                                                            sx={{
                                                                width: 24,
                                                                height: 24,
                                                                borderRadius: '50%',
                                                                backgroundColor: theme.palette.secondary.main,
                                                                display: 'flex',
                                                                alignItems: 'center',
                                                                justifyContent: 'center',
                                                                flexShrink: 0,
                                                                color: 'white',
                                                                fontSize: '0.8rem',
                                                                fontWeight: 'bold'
                                                            }}
                                                        >
                                                            ✓
                                                        </Box>
                                                        <Typography>
                                                            {item}
                                                        </Typography>
                                                    </Box>
                                                </Grid>
                                            ))}
                                        </Grid>
                                    </Box>
                                </Box>
                            </Box>
                        );
                    }

                    // Other sections
                    const content = data?.content[section.id];
                    if (!content) return null;

                    return (
                    <Box key={section.id} mb={4}>
                            <Typography variant="h5" gutterBottom>
                            {section.title}
                            </Typography>
                            <Paper sx={{ p: 3 }}>
                                {content.description && (
                                    <Typography paragraph>
                                        {content.description}
                                    </Typography>
                                )}

                                {content.diagram_url && (
                                    <Box mb={2}>
                                        <img 
                                            src={content.diagram_url} 
                                            alt="Diagrama"
                                            style={{ maxWidth: '100%', height: 'auto' }}
                                        />
                                    </Box>
                                )}

                            {content.team_members && Array.isArray(content.team_members) && (
                                    <Grid container spacing={2}>
                                        {content.team_members.map((member: any, index: number) => (
                                            <Grid item xs={12} sm={6} md={4} key={index}>
                                                <Card>
                                                    <CardContent>
                                                        <Typography variant="h6">{member.name}</Typography>
                                                        <Typography color="textSecondary">{member.role}</Typography>
                                                    </CardContent>
                                                </Card>
                                            </Grid>
                                        ))}
                                    </Grid>
                                )}

                                {content.timeline_data && (
                                    <Box>
                                        {/* Renderizar timeline conforme os dados */}
                                    </Box>
                                )}

                                {content.costs && (
                                    <TableContainer>
                                        <Table>
                                            <TableHead>
                                                <TableRow>
                                                    <TableCell>Item</TableCell>
                                                    <TableCell align="right">Valor</TableCell>
                                                </TableRow>
                                            </TableHead>
                                            <TableBody>
                                                {Object.entries(content.costs).map(([item, value]: [string, any]) => (
                                                    <TableRow key={item}>
                                                        <TableCell>{item}</TableCell>
                                                        <TableCell align="right">{formatCurrency(value)}</TableCell>
                                                    </TableRow>
                                                ))}
                                            </TableBody>
                                        </Table>
                                    </TableContainer>
                                )}
                            </Paper>
                        </Box>
                    );
                })}

            {/* Seções Dinâmicas */}
            {renderSections()}

            {/* Botão de Adicionar Seção (apenas para usuários logados) */}
            {isLoggedIn && (
                <Box mb={4}>
                    <Paper elevation={1} sx={{ p: 3 }}>
                        {creatingSection === false ? (
                            <Box display="flex" gap={2}>
                                <button 
                                    onClick={() => setCreatingSection('manual')}
                                    style={{
                                        padding: '10px 20px',
                                        backgroundColor: theme.palette.primary.main,
                                        color: 'white',
                                        border: 'none',
                                        borderRadius: '4px',
                                        cursor: 'pointer'
                                    }}
                                >
                                    + Adicionar Seção
                                </button>
                                <button 
                                    onClick={() => {
                                        setCreatingSection('ia');
                                        setNewSectionTitle('');
                                    }}
                                    style={{
                                        padding: '10px 20px',
                                        backgroundColor: theme.palette.secondary.main,
                                        color: 'white',
                                        border: 'none',
                                        borderRadius: '4px',
                                        cursor: 'pointer'
                                    }}
                                >
                                    + Adicionar com IA
                                </button>
                            </Box>
                        ) : (
                            <Box>
                                <input
                                    type="text"
                                    placeholder="Título ou tema da seção"
                                    value={newSectionTitle}
                                    onChange={e => setNewSectionTitle(e.target.value)}
                                    style={{ 
                                        width: '100%', 
                                        padding: '10px', 
                                        marginBottom: '10px',
                                        border: '1px solid #ccc',
                                        borderRadius: '4px'
                                    }}
                                />
                                {creatingSection === 'ia' && (
                                    <>
                                        <input
                                            type="text"
                                            placeholder="Tema ou instrução para a seção (ex: Desenho de arquitetura da solução)"
                                            value={newSectionPrompt}
                                            onChange={e => setNewSectionPrompt(e.target.value)}
                                            required
                                            style={{
                                                width: '100%',
                                                padding: '10px',
                                                marginBottom: '10px',
                                                border: '1px solid #ccc',
                                                borderRadius: '4px'
                                            }}
                                        />
                                        <Typography variant="body2" color="textSecondary" mb={2}>
                                            A seção será criada automaticamente com visual bonito, imagens e formatação profissional.
                                        </Typography>
                                    </>
                                )}
                                <Box display="flex" gap={2}>
                                    <button 
                                        onClick={() => handleCreateSection(creatingSection === 'ia')}
                                        disabled={!newSectionPrompt}
                                        style={{
                                            padding: '10px 20px',
                                            backgroundColor: theme.palette.primary.main,
                                            color: 'white',
                                            border: 'none',
                                            borderRadius: '4px',
                                            cursor: 'pointer',
                                            opacity: !newSectionPrompt ? 0.5 : 1
                                        }}
                                    >
                                        {creatingSection === 'ia' ? 'Criar com IA' : 'Criar Seção'}
                                    </button>
                                    <button 
                                        onClick={() => {
                                            setCreatingSection(false);
                                            setNewSectionTitle('');
                                            setNewSectionContent('');
                                            setNewSectionPrompt('');
                                        }}
                                        style={{
                                            padding: '10px 20px',
                                            backgroundColor: '#666',
                                            color: 'white',
                                            border: 'none',
                                            borderRadius: '4px',
                                            cursor: 'pointer'
                                        }}
                                    >
                                        Cancelar
                                    </button>
                                </Box>
                            </Box>
                        )}
                    </Paper>
                </Box>
            )}

            {/* Final Section - After all sections */}
            <Box 
                component={Paper} 
                elevation={0}
                sx={{
                    mb: 4,
                    p: 6,
                    backgroundColor: theme.palette.primary.main,
                    borderRadius: 2,
                    position: 'relative',
                    overflow: 'hidden',
                    color: 'white',
                    textAlign: 'center',
                    minHeight: '40vh',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    '&::before': {
                        content: '""',
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        backgroundImage: 'url(/fundo.png)',
                        backgroundSize: 'cover',
                        backgroundPosition: 'center',
                        opacity: 0.1,
                        zIndex: 0
                    }
                }}
            >
                <Box sx={{ position: 'relative', zIndex: 1, maxWidth: '800px', mx: 'auto' }}>
                    <Typography 
                        variant="h3" 
                        component="h2" 
                        sx={{ 
                            fontWeight: 'bold',
                            mb: 4
                        }}
                    >
                        Vamos Trabalhar Juntos?
                    </Typography>
                    <Typography 
                        variant="h6"
                        sx={{ 
                            opacity: 0.9,
                            maxWidth: '600px',
                            mx: 'auto',
                            lineHeight: 1.8,
                            fontWeight: 'normal'
                        }}
                    >
                        A Qaracter está pronta para ser sua parceira nesta jornada de transformação. 
                        Nossa experiência, metodologia comprovada e equipe especializada garantem 
                        a entrega de resultados excepcionais para seu negócio.
                    </Typography>
                </Box>
            </Box>

                {/* Footer */}
                <Box mt={8} pt={2} borderTop={1} borderColor="divider">
                    <Typography variant="body2" color="textSecondary" align="center">
                        © {new Date().getFullYear()} Qaracter. Todos os direitos reservados.
                    </Typography>
            </Box>
        </Container>

        {/* Actions Menu */}
        <Box
            sx={{
                position: 'fixed',
                bottom: 16,
                right: 16,
                zIndex: 1000
            }}
        >
            <IconButton
                onClick={handleClick}
                sx={{
                    backgroundColor: theme.palette.primary.main,
                    color: 'white',
                    '&:hover': {
                        backgroundColor: theme.palette.primary.dark,
                    },
                    width: 56,
                    height: 56
                }}
            >
                <MoreVertIcon />
            </IconButton>
            <Menu
                anchorEl={anchorEl}
                open={open}
                onClose={handleClose}
                anchorOrigin={{
                    vertical: 'top',
                    horizontal: 'left',
                }}
                transformOrigin={{
                    vertical: 'bottom',
                    horizontal: 'left',
                }}
            >
                {/* Troca de idioma removida: só a capa era traduzida; o conteúdo da proposta é escrito em português */}
                <MenuItem onClick={exportToPDF} disabled={exporting}>
                    <PdfIcon sx={{ mr: 1 }} />
                    {exporting ? 'Exportando...' : t('proposal.actions.exportPdf')}
                </MenuItem>
            </Menu>
        </Box>

        {/* Loading Backdrop */}
        <Backdrop
            sx={{ 
                color: '#fff', 
                zIndex: theme.zIndex.drawer + 1,
                flexDirection: 'column',
                gap: 2
            }}
            open={exporting}
        >
            <CircularProgress color="inherit" />
            <Typography variant="h6">
                Exportando PDF...
            </Typography>
        </Backdrop>
    </>
    );
};

export default SharedProposalPage; 